import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, X } from "lucide-react";
import { groupName, groupTint, groupsOf } from "@/lib/leadGroups";

/** Grouped multi-select filter (PRD v2.0 §5.3 / §5.4).
 *  Select All with live n/total count, per-group select-all with indeterminate
 *  state, colour dots, label search. Renders flat when the catalogue has no
 *  groups. `value` is an array of option values ([] = no filter). */
export default function GroupedMultiSelect({
  value = [], onChange, options = [], label = "Options",
  allLabel = "All options", testid = "multi-select", className = "",
  width = "w-[240px]", pinned = null,
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");

  const groups = useMemo(() => groupsOf(options), [options]);
  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    return options.filter((o) => !t || (o.label || o.value || "").toLowerCase().includes(t));
  }, [options, term]);
  const sections = useMemo(() => {
    if (!groups.length) return [{ key: null, items: filtered }];
    return groups
      .map((g) => ({ key: g, items: filtered.filter((o) => (o.ownership_group || o.group) === g) }))
      .filter((s) => s.items.length > 0);
  }, [groups, filtered]);

  const selectedSet = new Set(value);
  const pinnedSelected = !!pinned && selectedSet.has(pinned.value);
  const showPinned = !!pinned && (!term.trim() || pinned.label.toLowerCase().includes(term.trim().toLowerCase()));
  const total = options.length;
  const codeCount = value.filter((v) => !pinned || v !== pinned.value).length;
  const count = value.length;
  // "Select All" covers catalogue codes only — the pinned entry is never part of it.
  const allSelected = codeCount > 0 && codeCount === total && !pinnedSelected;

  const toggle = (v) => {
    const next = selectedSet.has(v) ? value.filter((x) => x !== v) : [...value, v];
    onChange(next);
  };
  const toggleAll = () => {
    const keep = pinnedSelected ? [pinned.value] : [];
    onChange(codeCount > 0 ? keep : [...keep, ...options.map((o) => o.value)]);
  };
  const toggleGroup = (items) => {
    const vals = items.map((o) => o.value);
    const every = vals.every((v) => selectedSet.has(v));
    onChange(every ? value.filter((v) => !vals.includes(v)) : [...new Set([...value, ...vals])]);
  };

  const summary = count === 0 || allSelected
    ? allLabel
    : count === 1
      ? (pinnedSelected ? pinned.label : (options.find((o) => o.value === value[0])?.label || value[0]))
      : `${count} ${label.toLowerCase()}`;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTerm(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline"
          className={`h-8 justify-between font-normal text-xs ${width} ${className}`}
          data-testid={testid}
        >
          <span className={`truncate ${count > 0 && !allSelected ? "text-slate-900" : "text-slate-500"}`}>
            {summary}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {count > 0 && !allSelected && (
              <span
                role="button" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onChange([]); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange([]); } }}
                className="text-slate-400 hover:text-rose-600"
                data-testid={`${testid}-clear`}
              >
                <X className="w-3 h-3" />
              </span>
            )}
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[300px]">
        <div className="p-2 border-b border-slate-100 space-y-2">
          <Input
            autoFocus value={term} onChange={(e) => setTerm(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`} className="h-7 text-xs"
            data-testid={`${testid}-search`}
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <Checkbox
                checked={allSelected ? true : (codeCount > 0 ? "indeterminate" : false)}
                onCheckedChange={toggleAll}
                data-testid={`${testid}-select-all`}
              />
              Select All
            </label>
            <span className="text-[10px] font-mono text-slate-400" data-testid={`${testid}-count`}>
              {codeCount}/{total}
            </span>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1" data-testid={`${testid}-list`}>
          {showPinned && (
            <label
              className="flex items-center gap-2 px-3 py-1.5 mb-1 text-xs cursor-pointer hover:bg-slate-50 border-b border-slate-100"
              data-testid={`${testid}-option-${pinned.value}`}
            >
              <Checkbox
                checked={pinnedSelected}
                onCheckedChange={() => toggle(pinned.value)}
              />
              <span className="inline-block w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: pinned.color || "#94A3B8" }} />
              <span className="truncate text-slate-700 italic">{pinned.label}</span>
            </label>
          )}
          {sections.length === 0 && !showPinned && (
            <div className="px-3 py-6 text-center text-xs text-slate-400">No matches</div>
          )}
          {sections.map((s) => {
            const vals = s.items.map((o) => o.value);
            const every = vals.every((v) => selectedSet.has(v));
            const some = !every && vals.some((v) => selectedSet.has(v));
            return (
              <div key={s.key || "flat"}>
                {s.key && (
                  <label
                    className={`flex items-center gap-2 px-3 py-1 cursor-pointer text-[10px] font-semibold uppercase tracking-wider ${groupTint(s.key)}`}
                    data-testid={`${testid}-group-${s.key}`}
                  >
                    <Checkbox
                      checked={every ? true : (some ? "indeterminate" : false)}
                      onCheckedChange={() => toggleGroup(s.items)}
                      data-testid={`${testid}-group-check-${s.key}`}
                    />
                    {groupName(s.key)}
                  </label>
                )}
                {s.items.map((o) => (
                  <label
                    key={o.value}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50"
                    data-testid={`${testid}-option-${o.value}`}
                  >
                    <Checkbox
                      checked={selectedSet.has(o.value)}
                      onCheckedChange={() => toggle(o.value)}
                    />
                    {o.color && (
                      <span className="inline-block w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: o.color }} />
                    )}
                    <span className="truncate text-slate-700">{o.label || o.value}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
